import React, { useMemo, useState } from 'react';
import { Button, Spinner } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

/**
 * The step between "file uploaded" and "records changed".
 *
 * Every comparable product warns people to test an import on two or three rows first, because
 * nothing tells them what a file will do until it has already done it. This screen is the answer to
 * that: the server validates the sheet without writing anything, and the user sees exactly how many
 * records will be created, how many updated, and every problem found — before deciding.
 *
 * @param {object} props
 * @param {object} props.job the import job being previewed
 * @param {boolean} props.busy whether a request is in flight
 * @param {function} props.onConfirm confirm the import
 * @param {function} props.onCancel abandon the preview
 * @param {function} props.onClose close after a finished import
 * @param {function} props.fetchAllIssues loads the complete issue list for download
 * @param {object} props.columnLabels canonical column key → header shown to the user
 */
const ImportPreview = ({ job, busy, onConfirm, onCancel, onClose, fetchAllIssues, columnLabels = {} }) => {
    const { t } = useTranslation();
    const [downloading, setDownloading] = useState(false);

    const issues = useMemo(() => job?.issues || [], [job]);

    /**
     * Counts the lookup rows the import would create as a side effect. People need to know an
     * import will add six vendors and a location before it does, not after.
     */
    const sideEffects = useMemo(() => {
        const counts = { assetTypes: new Set(), vendors: new Set(), locations: new Set() };
        issues.forEach((issue) => {
            const name = issue.params?.name;
            if (!name) return;
            if (issue.code === 'asset_type_will_be_created') counts.assetTypes.add(name);
            if (issue.code === 'vendor_will_be_created') counts.vendors.add(name);
            if (issue.code === 'location_will_be_created') counts.locations.add(name);
        });
        return {
            assetTypes: counts.assetTypes.size,
            vendors: counts.vendors.size,
            locations: counts.locations.size,
        };
    }, [issues]);

    const columnLabel = (key) => (key ? (columnLabels[key] || key) : '—');

    const issueText = (issue) =>
        t(`business.import_job.issue.${issue.code}`, { ...issue.params, defaultValue: issue.code });

    const handleDownloadIssues = async () => {
        setDownloading(true);
        try {
            const all = await fetchAllIssues();
            const rows = all.map((issue) => ({
                [t('business.import_job.column_row', 'Row')]: issue.row ?? '',
                [t('business.import_job.column_record', 'Record')]: issue.rowLabel ?? '',
                [t('business.import_job.column_column', 'Column')]: columnLabel(issue.column),
                [t('business.import_job.column_severity', 'Severity')]: issue.severity === 'ERROR'
                    ? t('business.import_job.severity_error', 'Error')
                    : t('business.import_job.severity_warning', 'Warning'),
                [t('business.import_job.column_message', 'Message')]: issueText(issue),
            }));
            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet,
                t('business.import_job.issues_sheet', 'Import issues'));
            XLSX.writeFile(workbook, `import_issues_${job.id}.xlsx`);
        } finally {
            setDownloading(false);
        }
    };

    const Tile = ({ value, label, tone }) => (
        <div className="flex-fill text-center p-3 rounded" style={{ backgroundColor: tone.bg, minWidth: '110px' }}>
            <div style={{ fontSize: '24px', fontWeight: 600, color: tone.fg }}>{value ?? 0}</div>
            <div className="text-muted" style={{ fontSize: '13px' }}>{label}</div>
        </div>
    );

    if (job.status === 'VALIDATING') {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="mt-3 text-dark">
                    {t('business.import_job.validating', 'Checking your file…')}
                </div>
                <div className="text-muted" style={{ fontSize: '13px' }}>
                    {t('business.import_job.rows_read', '{{count}} rows read', { count: job.totalRows || 0 })}
                </div>
            </div>
        );
    }

    if (job.status === 'COMMITTING') {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <div className="mt-3 text-dark">
                    {t('business.import_job.committing', 'Applying your changes…')}
                </div>
                <div className="text-muted" style={{ fontSize: '13px' }}>
                    {t('business.import_job.committing_hint',
                        'This runs on the server. You can close this window — the import will finish.')}
                </div>
            </div>
        );
    }

    if (job.status === 'COMPLETED') {
        const created = job.result?.createdIds?.length || 0;
        const updated = job.result?.updatedIds?.length || 0;
        return (
            <div className="text-center py-5">
                <FeatherIcon icon="check-circle" size={40} className="text-success" />
                <div className="mt-3 text-dark fw-semibold">
                    {t('business.import_job.completed', 'Import complete')}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '14px' }}>
                    {t('business.import_job.completed_counts',
                        '{{created}} created, {{updated}} updated', { created, updated })}
                </div>
                <Button color="primary" className="mt-4" onClick={onClose}>
                    {t('shared.close', 'Close')}
                </Button>
            </div>
        );
    }

    if (job.status === 'FAILED' || job.status === 'EXPIRED' || job.status === 'CANCELLED') {
        return (
            <div className="text-center py-5">
                <FeatherIcon icon="alert-circle" size={40} className="text-danger" />
                <div className="mt-3 text-dark fw-semibold">
                    {job.status === 'EXPIRED'
                        ? t('business.import_job.expired', 'This import preview has expired. Upload the file again.')
                        : t('business.import_job.failed', 'The import did not run. Nothing was changed.')}
                </div>
                {job.errorMessage && (
                    <div className="text-muted mt-2" style={{ fontSize: '13px' }}>{job.errorMessage}</div>
                )}
                <Button color="primary" className="mt-4" onClick={onClose}>
                    {t('shared.close', 'Close')}
                </Button>
            </div>
        );
    }

    // READY — the actual preview.
    const blocked = (job.errorRows || 0) > 0;
    return (
        <div>
            <div className="d-flex gap-3 mb-3 flex-wrap">
                <Tile value={job.createRows} tone={{ bg: '#EBF4FF', fg: '#2563EB' }}
                      label={t('business.import_job.to_create', 'To create')} />
                <Tile value={job.updateRows} tone={{ bg: '#F0FDF4', fg: '#16A34A' }}
                      label={t('business.import_job.to_update', 'To update')} />
                <Tile value={job.warningRows} tone={{ bg: '#FEFCE8', fg: '#CA8A04' }}
                      label={t('business.import_job.warnings', 'Warnings')} />
                <Tile value={job.errorRows} tone={{ bg: '#FEF2F2', fg: '#DC2626' }}
                      label={t('business.import_job.errors', 'Errors')} />
            </div>

            {(sideEffects.assetTypes > 0 || sideEffects.vendors > 0 || sideEffects.locations > 0) && (
                <div className="alert alert-info py-2 mb-3" style={{ fontSize: '13px' }}>
                    {t('business.import_job.also_creates',
                        'This import will also create: {{types}} asset type(s), {{vendors}} supplier(s), {{locations}} location(s).',
                        {
                            types: sideEffects.assetTypes,
                            vendors: sideEffects.vendors,
                            locations: sideEffects.locations,
                        })}
                </div>
            )}

            {blocked && (
                <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '13px' }}>
                    {t('business.import_job.blocked',
                        'Fix the errors below in your file and upload it again. Nothing has been changed.')}
                </div>
            )}

            {issues.length === 0 ? (
                <div className="text-center text-muted py-4" style={{ fontSize: '14px' }}>
                    {t('business.import_job.no_issues', 'No issues found — ready to import.')}
                </div>
            ) : (
                <>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="text-muted" style={{ fontSize: '13px' }}>
                            {job.hasMoreIssues
                                ? t('business.import_job.issues_truncated',
                                    'Showing the first {{shown}} of {{total}} issues',
                                    { shown: issues.length, total: job.totalIssues })
                                : t('business.import_job.issues_count', '{{count}} issue(s)', { count: issues.length })}
                        </span>
                        <Button outline color="secondary" size="sm" disabled={downloading}
                                onClick={handleDownloadIssues}>
                            <FeatherIcon icon="download" size={14} className="me-1" />
                            {t('business.import_job.download_issues', 'Download issues')}
                        </Button>
                    </div>
                    {/* Wide content scrolls inside its own box so the modal never scrolls sideways. */}
                    <div style={{ maxHeight: '320px', overflow: 'auto' }}>
                        <table className="table table-sm mb-0">
                            <thead style={{ position: 'sticky', top: 0, background: '#fff' }}>
                            <tr>
                                <th style={{ width: '70px' }}>{t('business.import_job.column_row', 'Row')}</th>
                                <th>{t('business.import_job.column_record', 'Record')}</th>
                                <th>{t('business.import_job.column_column', 'Column')}</th>
                                <th>{t('business.import_job.column_message', 'Message')}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {issues.map((issue, index) => (
                                <tr key={`${issue.row}-${issue.code}-${index}`}>
                                    <td>{issue.row ?? '—'}</td>
                                    <td className="text-truncate" style={{ maxWidth: '160px' }}>
                                        {issue.rowLabel || '—'}
                                    </td>
                                    <td>{columnLabel(issue.column)}</td>
                                    <td>
                                        <FeatherIcon
                                            icon={issue.severity === 'ERROR' ? 'x-circle' : 'alert-triangle'}
                                            size={14}
                                            className={`me-2 ${issue.severity === 'ERROR' ? 'text-danger' : 'text-warning'}`}
                                        />
                                        {issueText(issue)}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                <Button outline color="secondary" onClick={onCancel} disabled={busy}>
                    {t('shared.cancel', 'Cancel')}
                </Button>
                <Button color="primary" onClick={onConfirm} disabled={busy || blocked}>
                    {busy && <Spinner size="sm" className="me-2" />}
                    {t('business.import_job.confirm', 'Confirm import')}
                </Button>
            </div>
        </div>
    );
};

export default ImportPreview;
